import { useMemo } from 'react';

import type { GeneratedChecklistRow } from './checklistRows';
import type { DealOptionRowView } from './tabs/dealOptionsInclusions/types';
import type { ApprovalQueueItem, ChecklistCategoryId, ChecklistRowsByCategory } from './types';
import type { VisibleChecklistRows } from './useVisibleChecklistRows';

interface Input extends Pick<
  ChecklistRowsByCategory,
  | 'baseSurchargesRows'
  | 'blackoutRows'
  | 'bundledExperienceRows'
  | 'campaignRows'
  | 'capacityRows'
  | 'roomRows'
  | 'dealOptionsInclusionsRows'
  | 'discountsMarginRows'
  | 'egsAgeCoverageRows'
  | 'marginRows'
  | 'setupRows'
  | 'surchargeTableRows'
  | 'taxesPaymentsRows'
> {
  activeCategoryId: ChecklistCategoryId;
  dealOptionsInclusionsDealRows: Array<DealOptionRowView>;
  isApprovalQueueMode: boolean;
  visibleQueueItems: Array<ApprovalQueueItem>;
  visibleRows: VisibleChecklistRows;
}

export function useActiveCategoryRows({
  activeCategoryId,
  baseSurchargesRows,
  blackoutRows,
  bundledExperienceRows,
  campaignRows,
  capacityRows,
  roomRows,
  dealOptionsInclusionsDealRows,
  dealOptionsInclusionsRows,
  discountsMarginRows,
  egsAgeCoverageRows,
  isApprovalQueueMode,
  marginRows,
  setupRows,
  surchargeTableRows,
  taxesPaymentsRows,
  visibleQueueItems,
  visibleRows,
}: Input) {
  const activeExpandableItems = useMemo<Array<{ id: string; defaultOpen?: boolean }>>(() => {
    if (isApprovalQueueMode) {
      return visibleQueueItems.map((item) => ({ id: item.id }));
    }

    switch (activeCategoryId) {
      case 'setup':
        return visibleRows.visibleSetupRows.map((row) => ({ id: row.id, defaultOpen: true }));
      case 'campaign':
        return visibleRows.visibleCampaignRows.map((row) => ({ id: row.id, defaultOpen: true }));
      case 'discountsMargin':
        return visibleRows.visibleDiscountsMarginRows.map((row) => ({ id: row.id, defaultOpen: true }));
      case 'taxesPayments':
        return visibleRows.visibleTaxesPaymentsRows.map((row) => ({ id: row.id }));
      case 'dealOptionsInclusions':
        return dealOptionsInclusionsDealRows.map((deal) => ({ id: deal.id, defaultOpen: true }));
      case 'rooms':
        return visibleRows.visibleRoomRows.map((row) => ({ id: row.id, defaultOpen: true }));
      case 'egs':
        return [
          ...visibleRows.visibleMarginRows
            .filter((row) => row.beddingType || row.inclusions || row.collectedBy)
            .map((row) => ({ id: row.id })),
          ...visibleRows.visibleEgsAgeCoverageRows.map((row) => ({ id: row.id })),
        ];
      case 'surcharges':
        return visibleRows.visibleSurchargeTableRows
          .filter((row) => row.dealOptionNames.length > 0)
          .map((row) => ({ id: row.id }));
      case 'bundledExperiences':
        return visibleRows.visibleBundledExperienceRows
          .filter((row) => row.linkedDealOptions.length > 0)
          .map((row) => ({ id: row.id }));
      default:
        return [];
    }
  }, [activeCategoryId, dealOptionsInclusionsDealRows, isApprovalQueueMode, visibleQueueItems, visibleRows]);

  const activeTabRows = useMemo<Array<GeneratedChecklistRow>>(() => {
    switch (activeCategoryId) {
      case 'setup':
        return setupRows;
      case 'campaign':
        return campaignRows;
      case 'discountsMargin':
        return discountsMarginRows;
      case 'taxesPayments':
        return taxesPaymentsRows;
      case 'dealOptionsInclusions':
        return dealOptionsInclusionsRows;
      case 'rooms':
        return roomRows;
      case 'surcharges':
        return [...surchargeTableRows, ...baseSurchargesRows];
      case 'blackoutDates':
        return blackoutRows;
      case 'egs':
        return [...marginRows, ...capacityRows, ...egsAgeCoverageRows];
      case 'bundledExperiences':
        return bundledExperienceRows;
      default:
        return [];
    }
  }, [
    activeCategoryId,
    baseSurchargesRows,
    blackoutRows,
    bundledExperienceRows,
    campaignRows,
    capacityRows,
    roomRows,
    discountsMarginRows,
    dealOptionsInclusionsRows,
    egsAgeCoverageRows,
    marginRows,
    setupRows,
    surchargeTableRows,
    taxesPaymentsRows,
  ]);

  return { activeExpandableItems, activeTabRows };
}
